import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";

type KodamaEdge = "top" | "right" | "bottom" | "left";
type MotionPreset =
  | "edge-peek"
  | "fade-in-place"
  | "drift-across"
  | "quick-peek-duck"
  | "bobble"
  | "rotate-tilt-in"
  | "shy-half-emerge";

type KodamaSpot = {
  edge: KodamaEdge;
  x: string;
  y: string;
  fromX: string;
  fromY: string;
  peekX: string;
  peekY: string;
};

type WatcherState = KodamaSpot & {
  id: number;
  driftX: string;
  driftY: string;
  opacity: number;
  rotation: number;
  shyX: string;
  shyY: string;
  size: number;
  sprite: string;
  duration: number;
  flip: 1 | -1;
  motion: MotionPreset;
};

const watcherSprites = Array.from(
  { length: 15 },
  (_, index) =>
    `/kodamas/watchers/kodama-${String(index + 1).padStart(2, "0")}.png`,
);

const desktopSpots: KodamaSpot[] = [
  {
    edge: "top",
    x: "14vw",
    y: "58px",
    fromX: "-50%",
    fromY: "-92%",
    peekX: "-50%",
    peekY: "-12%",
  },
  {
    edge: "top",
    x: "36vw",
    y: "58px",
    fromX: "-50%",
    fromY: "-94%",
    peekX: "-50%",
    peekY: "-14%",
  },
  {
    edge: "top",
    x: "66vw",
    y: "58px",
    fromX: "-50%",
    fromY: "-93%",
    peekX: "-50%",
    peekY: "-15%",
  },
  {
    edge: "top",
    x: "88vw",
    y: "58px",
    fromX: "-50%",
    fromY: "-92%",
    peekX: "-50%",
    peekY: "-13%",
  },
  {
    edge: "left",
    x: "0vw",
    y: "18vh",
    fromX: "-72%",
    fromY: "-50%",
    peekX: "-36%",
    peekY: "-50%",
  },
  {
    edge: "left",
    x: "0vw",
    y: "44vh",
    fromX: "-74%",
    fromY: "-50%",
    peekX: "-38%",
    peekY: "-50%",
  },
  {
    edge: "left",
    x: "0vw",
    y: "72vh",
    fromX: "-72%",
    fromY: "-50%",
    peekX: "-34%",
    peekY: "-50%",
  },
  {
    edge: "right",
    x: "100vw",
    y: "18vh",
    fromX: "-28%",
    fromY: "-50%",
    peekX: "-64%",
    peekY: "-50%",
  },
  {
    edge: "right",
    x: "100vw",
    y: "46vh",
    fromX: "-26%",
    fromY: "-50%",
    peekX: "-62%",
    peekY: "-50%",
  },
  {
    edge: "right",
    x: "100vw",
    y: "74vh",
    fromX: "-27%",
    fromY: "-50%",
    peekX: "-65%",
    peekY: "-50%",
  },
  {
    edge: "bottom",
    x: "20vw",
    y: "100vh",
    fromX: "-50%",
    fromY: "-28%",
    peekX: "-50%",
    peekY: "-64%",
  },
  {
    edge: "bottom",
    x: "50vw",
    y: "100vh",
    fromX: "-50%",
    fromY: "-26%",
    peekX: "-50%",
    peekY: "-62%",
  },
  {
    edge: "bottom",
    x: "80vw",
    y: "100vh",
    fromX: "-50%",
    fromY: "-27%",
    peekX: "-50%",
    peekY: "-65%",
  },
];

const mobileSpots: KodamaSpot[] = [
  {
    edge: "left",
    x: "0vw",
    y: "22vh",
    fromX: "-78%",
    fromY: "-50%",
    peekX: "-48%",
    peekY: "-50%",
  },
  {
    edge: "right",
    x: "100vw",
    y: "22vh",
    fromX: "-22%",
    fromY: "-50%",
    peekX: "-52%",
    peekY: "-50%",
  },
  {
    edge: "left",
    x: "0vw",
    y: "82vh",
    fromX: "-78%",
    fromY: "-50%",
    peekX: "-46%",
    peekY: "-50%",
  },
  {
    edge: "right",
    x: "100vw",
    y: "82vh",
    fromX: "-22%",
    fromY: "-50%",
    peekX: "-54%",
    peekY: "-50%",
  },
  {
    edge: "bottom",
    x: "22vw",
    y: "100vh",
    fromX: "-50%",
    fromY: "-22%",
    peekX: "-50%",
    peekY: "-54%",
  },
  {
    edge: "bottom",
    x: "78vw",
    y: "100vh",
    fromX: "-50%",
    fromY: "-22%",
    peekX: "-50%",
    peekY: "-56%",
  },
];

function randomBetween(min: number, max: number) {
  return min + Math.random() * (max - min);
}

/**
 * Rotation (degrees) that makes the sprite lean toward the screen center.
 * Positive = clockwise (top leans right). Negative = counter-clockwise.
 */
function rotationTowardCenter(edge: KodamaEdge, x: string): number {
  if (edge === "left")   return randomBetween(22, 38);   // lean right → center
  if (edge === "right")  return randomBetween(-38, -22); // lean left  → center
  // top / bottom: lean toward horizontal center
  const vw = parseFloat(x);
  const dir = vw < 50 ? 1 : -1;
  return dir * randomBetween(8, 18);
}

function nextDelay() {
  return randomBetween(5000, 16000);
}

function jitterDuration(duration: number) {
  return Math.round(duration * randomBetween(0.8, 1.2));
}

function interpolatePercent(from: string, to: string, amount: number) {
  const start = Number.parseFloat(from);
  const end = Number.parseFloat(to);
  return `${start + (end - start) * amount}%`;
}

function driftForEdge(edge: KodamaEdge) {
  const distance = Math.round(randomBetween(80, 140));
  if (edge === "top" || edge === "bottom") {
    return {
      driftX: `${Math.random() > 0.5 ? distance : -distance}px`,
      driftY: "0px",
    };
  }
  return {
    driftX: "0px",
    driftY: `${Math.random() > 0.5 ? distance : -distance}px`,
  };
}

function nextWatcher(
  spots: KodamaSpot[],
  lastSpotIndex: number,
  lastSpriteIndex: number,
  id: number,
  isMobile: boolean,
): [WatcherState, number, number] {
  let spotIndex = Math.floor(Math.random() * spots.length);
  if (spotIndex === lastSpotIndex) {
    spotIndex = (spotIndex + 1) % spots.length;
  }

  let spriteIndex = Math.floor(Math.random() * watcherSprites.length);
  if (spriteIndex === lastSpriteIndex) {
    spriteIndex = (spriteIndex + 5) % watcherSprites.length;
  }

  const spot = spots[spotIndex];
  const isSideEdge = spot.edge === "left" || spot.edge === "right";
  const motionPresets: MotionPreset[] = [
    "edge-peek",
    "fade-in-place",
    "drift-across",
    "quick-peek-duck",
    "bobble",
    "rotate-tilt-in",
    "shy-half-emerge",
  ];
  const motion =
    motionPresets[Math.floor(Math.random() * motionPresets.length)];
  const baseDurationByMotion: Record<MotionPreset, number> = {
    "edge-peek": 8000,
    "fade-in-place": 7000,
    "drift-across": 9000,
    "quick-peek-duck": 3200,
    bobble: 8000,
    "rotate-tilt-in": 7600,
    "shy-half-emerge": 9200,
  };
  const { driftX, driftY } = driftForEdge(spot.edge);

  return [
    {
      ...spot,
      id,
      driftX,
      driftY,
      duration: jitterDuration(baseDurationByMotion[motion]),
      flip: 1,
      motion,
      opacity: randomBetween(0.7, 0.95),
      rotation: rotationTowardCenter(spot.edge, spot.x),
      shyX: interpolatePercent(spot.fromX, spot.peekX, 0.45),
      shyY: interpolatePercent(spot.fromY, spot.peekY, 0.45),
      size: Math.round(
        isMobile ? randomBetween(32, 54) : randomBetween(44, 72),
      ),
      sprite: watcherSprites[spriteIndex],
    },
    spotIndex,
    spriteIndex,
  ];
}

export function KodamaWatchers() {
  const [watchers, setWatchers] = useState<WatcherState[]>([]);
  const removeTimeouts = useRef<number[]>([]);

  useEffect(() => {
    const isMobile = window.matchMedia("(max-width: 640px)").matches;
    const spots = isMobile ? mobileSpots : desktopSpots;
    let appearTimeout = 0;
    let lastSpotIndex = -1;
    let lastSpriteIndex = -1;
    let nextId = 1;

    function schedule(delay: number) {
      appearTimeout = window.setTimeout(() => {
        const [next, spotIndex, spriteIndex] = nextWatcher(
          spots,
          lastSpotIndex,
          lastSpriteIndex,
          nextId,
          isMobile,
        );
        lastSpotIndex = spotIndex;
        lastSpriteIndex = spriteIndex;
        nextId += 1;

        setWatchers((current) => [...current.slice(-1), next]);

        const removeTimeout = window.setTimeout(() => {
          setWatchers((current) =>
            current.filter((watcher) => watcher.id !== next.id),
          );
        }, next.duration);
        removeTimeouts.current.push(removeTimeout);

        schedule(nextDelay());
      }, delay);
    }

    schedule(900 + Math.random() * 900);

    return () => {
      window.clearTimeout(appearTimeout);
      for (const timeout of removeTimeouts.current) {
        window.clearTimeout(timeout);
      }
      removeTimeouts.current = [];
    };
  }, []);

  return (
    <div className="kodama-watchers" aria-hidden="true">
      {watchers.map((watcher) => (
        <img
          key={watcher.id}
          className={`kodama-watcher kodama-watcher--${watcher.edge} kodama-watcher--${watcher.motion}`}
          data-motion={watcher.motion}
          src={watcher.sprite}
          alt=""
          draggable="false"
          style={
            {
              "--kodama-x": watcher.x,
              "--kodama-y": watcher.y,
              "--kodama-from-x": watcher.fromX,
              "--kodama-from-y": watcher.fromY,
              "--kodama-peek-x": watcher.peekX,
              "--kodama-peek-y": watcher.peekY,
              "--kodama-shy-x": watcher.shyX,
              "--kodama-shy-y": watcher.shyY,
              "--kodama-drift-x": watcher.driftX,
              "--kodama-drift-y": watcher.driftY,
              "--kodama-size": `${watcher.size}px`,
              "--kodama-duration": `${watcher.duration}ms`,
              "--kodama-opacity": watcher.opacity,
              "--kodama-rotation": `${watcher.rotation}deg`,
              "--kodama-flip": watcher.flip,
            } as CSSProperties
          }
        />
      ))}
    </div>
  );
}
